import { useNavigate } from 'react-router-dom'
import type { EntrySearchRow } from '../types'
import { StatusBadge, POSBadge } from './EvidenceBadges'

interface EntryCardProps {
  entry: EntrySearchRow
}

export function EntryCard({ entry }: EntryCardProps) {
  const navigate = useNavigate()

  return (
    <article
      className="dict-card p-5 space-y-3"
      onClick={() => navigate(`/entry/${entry.entry_id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/entry/${entry.entry_id}`) }}
      aria-label={`Entry: ${entry.magar_form_romanized} — ${entry.nepali_form_devanagari}`}
    >
      {/* Top row: Magar form + badges */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="deva text-2xl font-bold text-magar-700 leading-tight">
            {entry.magar_form_devanagari}
          </p>
          {entry.magar_form_romanized && (
            <p className="text-sm text-magar-600 italic mt-0.5">
              {entry.magar_form_romanized}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 shrink-0 mt-1">
          <StatusBadge status={entry.status} />
          <POSBadge pos={entry.pos} />
        </div>
      </div>

      {/* Nepali equivalent */}
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide shrink-0">
          Nepali
        </span>
        <span className="deva text-lg text-stone-700 font-medium">
          {entry.nepali_form_devanagari}
        </span>
        {entry.nepali_form_romanized && (
          <span className="text-sm text-stone-400 italic">
            {entry.nepali_form_romanized}
          </span>
        )}
      </div>

      {/* English gloss */}
      {entry.english_gloss && (
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide shrink-0">
            English
          </span>
          <span className="text-stone-500 italic text-sm">{entry.english_gloss}</span>
        </div>
      )}

      {/* Variant note */}
      {entry.magar_variant_note && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 border border-amber-100">
          Also: {entry.magar_variant_note}
        </p>
      )}
    </article>
  )
}
